import React, { useState, useEffect, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Button,
    Grid,
    Typography,
    Box,
    Paper,
    Divider,
    Chip,
    IconButton,
    alpha,
    useTheme,
    useMediaQuery,
    Card,
    CardContent,
    Stack,
    Tooltip,
    FormControlLabel,
    Radio,
    RadioGroup,
    Checkbox,
    FormGroup
} from '@mui/material';
import {
    Close as CloseIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    DragIndicator as DragIcon,
    Save as SaveIcon,
    Feedback as FeedbackIcon
} from '@mui/icons-material';

const FeedbackDialog = ({
    open,
    onClose,
    onSave,
    feedback = null,
    availableAssessments = []
}) => {
    const [formData, setFormData] = useState({
        feedback_name: '',
        assessment_id: '',
        assessment_name: '',
        program_code: '',
        template_name: '',
        status: 'Draft',
        questions: []
    });

    const [errors, setErrors] = useState({});
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Extract unique program codes from available assessments
    const availablePrograms = useMemo(() => {
        const programsMap = new Map();
        availableAssessments.forEach(assessment => {
            if (assessment.program_code && !programsMap.has(assessment.program_code)) {
                programsMap.set(assessment.program_code, {
                    code: assessment.program_code,
                    name: assessment.program_code // You can add program name if available
                });
            }
        });
        return Array.from(programsMap.values());
    }, [availableAssessments]);

    useEffect(() => {
        if (feedback) {
            setFormData({
                feedback_name: feedback.feedback_name || '',
                assessment_id: feedback.assessment_id || '',
                assessment_name: feedback.assessment_name || '',
                program_code: feedback.program_code || '',
                template_name: feedback.template_name || '',
                status: feedback.status || 'Draft',
                questions: feedback.questions || []
            });
        } else {
            resetForm();
        }
    }, [feedback, open]);

    const resetForm = () => {
        setFormData({
            feedback_name: '',
            assessment_id: '',
            assessment_name: '',
            program_code: '',
            template_name: '',
            status: 'Draft',
            questions: []
        });
        setErrors({});
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        // Clear error for this field
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: null
            }));
        }
    };

    const handleProgramChange = (programCode) => {
        setFormData(prev => ({
            ...prev,
            program_code: programCode
        }));
        // Clear error for program_code
        if (errors.program_code) {
            setErrors(prev => ({
                ...prev,
                program_code: null
            }));
        }
    };

    const addQuestion = () => {
        const newQuestion = {
            id: Date.now().toString(),
            question_text: '',
            question_type: 'text',
            is_required: false,
            options: []
        };
        setFormData(prev => ({
            ...prev,
            questions: [...prev.questions, newQuestion]
        }));
    };

    const removeQuestion = (questionId) => {
        setFormData(prev => ({
            ...prev,
            questions: prev.questions.filter(q => q.id !== questionId)
        }));
    };

    const updateQuestion = (questionId, field, value) => {
        setFormData(prev => ({
            ...prev,
            questions: prev.questions.map(q =>
                q.id === questionId ? { ...q, [field]: value } : q
            )
        }));
    };

    const addOption = (questionId) => {
        setFormData(prev => ({
            ...prev,
            questions: prev.questions.map(q =>
                q.id === questionId
                    ? {
                        ...q,
                        options: [...(q.options || []), { id: Date.now().toString(), value: '' }]
                    }
                    : q
            )
        }));
    };

    const removeOption = (questionId, optionId) => {
        setFormData(prev => ({
            ...prev,
            questions: prev.questions.map(q =>
                q.id === questionId
                    ? { ...q, options: q.options.filter(o => o.id !== optionId) }
                    : q
            )
        }));
    };

    const updateOption = (questionId, optionId, value) => {
        setFormData(prev => ({
            ...prev,
            questions: prev.questions.map(q =>
                q.id === questionId
                    ? {
                        ...q,
                        options: q.options.map(o =>
                            o.id === optionId ? { ...o, value } : o
                        )
                    }
                    : q
            )
        }));
    };

    const validate = () => {
        const newErrors = {};

        if (!formData.feedback_name?.trim()) {
            newErrors.feedback_name = 'Feedback name is required';
        }

        if (!formData.program_code) {
            newErrors.program_code = 'Please select a program';
        }

        if (formData.questions.length === 0) {
            newErrors.questions = 'At least one question is required';
        }

        // Validate each question
        formData.questions.forEach((q, index) => {
            if (!q.question_text?.trim()) {
                newErrors[`question_${q.id}`] = 'Question text is required';
            }
            if (['radio', 'checkbox', 'dropdown'].includes(q.question_type) && (!q.options || q.options.length === 0)) {
                newErrors[`question_${q.id}_options`] = 'At least one option is required for this question type';
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = () => {
        if (validate()) {
            onSave(formData);
        }
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const questionTypes = [
        { value: 'text', label: 'Text Input' },
        { value: 'textarea', label: 'Long Text' },
        { value: 'radio', label: 'Single Choice (Radio)' },
        { value: 'checkbox', label: 'Multiple Choice (Checkbox)' },
        { value: 'dropdown', label: 'Dropdown' },
        { value: 'rating', label: 'Rating (1-5)' },
        { value: 'date', label: 'Date' }
    ];

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
                sx: {
                    borderRadius: isMobile ? 0 : 2,
                    maxHeight: '90vh'
                }
            }}
        >
            <DialogTitle
                sx={{
                    background: 'linear-gradient(45deg, #0a0068 30%, #cc4049 90%)',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 2
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FeedbackIcon />
                    <Typography variant="h6" sx={{ fontFamily: 'Poppins', fontWeight: 600 }}>
                        {feedback ? 'Edit Feedback Form' : 'Create New Feedback Form'}
                    </Typography>
                </Box>
                <IconButton
                    onClick={handleClose}
                    size="small"
                    sx={{ color: 'white' }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: 3, pb: 2 }}>
                <Grid container spacing={3}>
                    {/* Basic Information */}
                    <Grid item xs={12}>
                        <Typography
                            variant="subtitle1"
                            sx={{
                                fontWeight: 600,
                                color: '#0a0068',
                                fontFamily: 'Poppins',
                                mt: 1,
                                mb: 0
                            }}
                        >
                            Basic Information
                        </Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Feedback Name"
                            value={formData.feedback_name}
                            onChange={(e) => handleInputChange('feedback_name', e.target.value)}
                            error={!!errors.feedback_name}
                            helperText={errors.feedback_name}
                            required
                            size="small"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    fontFamily: 'Poppins'
                                }
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormControl
                            fullWidth
                            size="small"
                            error={!!errors.program_code}
                            required
                        >
                            <InputLabel>Select Program</InputLabel>
                            <Select
                                value={formData.program_code}
                                label="Select Program"
                                onChange={(e) => handleProgramChange(e.target.value)}
                                sx={{
                                    fontFamily: 'Poppins'
                                }}
                            >
                                <MenuItem value="" disabled>
                                    <em>Choose a program</em>
                                </MenuItem>
                                {availablePrograms.map((program) => (
                                    <MenuItem
                                        key={program.code}
                                        value={program.code}
                                        sx={{ fontFamily: 'Poppins' }}
                                    >
                                        {program.name}
                                    </MenuItem>
                                ))}
                            </Select>
                            {errors.program_code && (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: 'error.main',
                                        fontFamily: 'Poppins',
                                        ml: 1.5,
                                        mt: 0.5,
                                        fontSize: '0.75rem'
                                    }}
                                >
                                    {errors.program_code}
                                </Typography>
                            )}
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Template Name"
                            value={formData.template_name}
                            onChange={(e) => handleInputChange('template_name', e.target.value)}
                            size="small"
                            disabled
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    fontFamily: 'Poppins',
                                    backgroundColor: alpha('#f5f5f5', 0.5)
                                }
                            }}
                            helperText="Auto-filled from selected assessment"
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={formData.status}
                                label="Status"
                                onChange={(e) => handleInputChange('status', e.target.value)}
                                sx={{
                                    fontFamily: 'Poppins'
                                }}
                            >
                                <MenuItem value="Draft">Draft</MenuItem>
                                <MenuItem value="Active">Active</MenuItem>
                                <MenuItem value="Closed">Closed</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Questions Section */}
                    <Grid item xs={12}>
                        <Divider sx={{ my: 2 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    fontWeight: 600,
                                    color: '#0a0068',
                                    fontFamily: 'Poppins'
                                }}
                            >
                                Questions
                            </Typography>
                        </Box>
                        {errors.questions && (
                            <Typography color="error" variant="caption" sx={{ fontFamily: 'Poppins', display: 'block', mb: 2 }}>
                                {errors.questions}
                            </Typography>
                        )}
                    </Grid>

                    {/* Question List */}
                    {formData.questions.map((question, index) => (
                        <Grid item xs={12} key={question.id}>
                            <Card
                                sx={{
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 2,
                                    boxShadow: 'none'
                                }}
                            >
                                <CardContent>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <DragIcon sx={{ color: '#9e9e9e', cursor: 'move' }} />
                                            <Typography
                                                variant="subtitle2"
                                                sx={{
                                                    fontWeight: 600,
                                                    color: '#0a0068',
                                                    fontFamily: 'Poppins'
                                                }}
                                            >
                                                Question {index + 1}
                                            </Typography>
                                        </Box>
                                        <IconButton
                                            onClick={() => removeQuestion(question.id)}
                                            size="small"
                                            sx={{ color: '#f44336' }}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>

                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Question Text"
                                                value={question.question_text}
                                                onChange={(e) => updateQuestion(question.id, 'question_text', e.target.value)}
                                                error={!!errors[`question_${question.id}`]}
                                                helperText={errors[`question_${question.id}`]}
                                                required
                                                size="small"
                                                multiline
                                                rows={2}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        fontFamily: 'Poppins'
                                                    }
                                                }}
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Question Type</InputLabel>
                                                <Select
                                                    value={question.question_type}
                                                    label="Question Type"
                                                    onChange={(e) => updateQuestion(question.id, 'question_type', e.target.value)}
                                                    sx={{
                                                        fontFamily: 'Poppins'
                                                    }}
                                                >
                                                    {questionTypes.map(type => (
                                                        <MenuItem key={type.value} value={type.value}>
                                                            {type.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={question.is_required}
                                                        onChange={(e) => updateQuestion(question.id, 'is_required', e.target.checked)}
                                                        sx={{
                                                            color: '#0a0068',
                                                            '&.Mui-checked': {
                                                                color: '#0a0068'
                                                            }
                                                        }}
                                                    />
                                                }
                                                label="Required"
                                                sx={{
                                                    '& .MuiFormControlLabel-label': {
                                                        fontFamily: 'Poppins',
                                                        fontSize: '0.875rem'
                                                    }
                                                }}
                                            />
                                        </Grid>

                                        {/* Options for radio, checkbox, dropdown */}
                                        {['radio', 'checkbox', 'dropdown'].includes(question.question_type) && (
                                            <Grid item xs={12}>
                                                <Box sx={{ pl: 2 }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                        <Typography variant="caption" sx={{ fontFamily: 'Poppins', color: '#666' }}>
                                                            Options
                                                        </Typography>
                                                        <Button
                                                            startIcon={<AddIcon />}
                                                            onClick={() => addOption(question.id)}
                                                            size="small"
                                                            sx={{
                                                                textTransform: 'none',
                                                                fontFamily: 'Poppins',
                                                                fontSize: '0.75rem'
                                                            }}
                                                        >
                                                            Add Option
                                                        </Button>
                                                    </Box>
                                                    {errors[`question_${question.id}_options`] && (
                                                        <Typography color="error" variant="caption" sx={{ fontFamily: 'Poppins', display: 'block', mb: 1 }}>
                                                            {errors[`question_${question.id}_options`]}
                                                        </Typography>
                                                    )}
                                                    {question.options?.map((option, optIndex) => (
                                                        <Box key={option.id} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                                                            <Typography variant="caption" sx={{ minWidth: 20 }}>
                                                                {optIndex + 1}.
                                                            </Typography>
                                                            <TextField
                                                                fullWidth
                                                                placeholder="Option value"
                                                                value={option.value}
                                                                onChange={(e) => updateOption(question.id, option.id, e.target.value)}
                                                                size="small"
                                                                sx={{
                                                                    '& .MuiOutlinedInput-root': {
                                                                        fontFamily: 'Poppins'
                                                                    }
                                                                }}
                                                            />
                                                            <IconButton
                                                                onClick={() => removeOption(question.id, option.id)}
                                                                size="small"
                                                                sx={{ color: '#f44336' }}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            </Grid>
                                        )}
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}

                    {/* Add Question Button at bottom */}
                    <Grid item xs={12}>
                        <Button
                            startIcon={<AddIcon />}
                            onClick={addQuestion}
                            variant="outlined"
                            fullWidth
                            sx={{
                                textTransform: 'none',
                                fontFamily: 'Poppins',
                                borderColor: '#0a0068',
                                color: '#0a0068',
                                py: 1.5,
                                borderStyle: 'dashed',
                                '&:hover': {
                                    borderColor: '#cc4049',
                                    color: '#cc4049',
                                    borderStyle: 'dashed',
                                    backgroundColor: alpha('#cc4049', 0.05)
                                }
                            }}
                        >
                            Add Question
                        </Button>
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e0e0e0' }}>
                <Button
                    onClick={handleClose}
                    sx={{
                        textTransform: 'none',
                        fontFamily: 'Poppins',
                        color: '#666'
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    startIcon={<SaveIcon />}
                    sx={{
                        textTransform: 'none',
                        fontFamily: 'Poppins',
                        background: 'linear-gradient(45deg, #0a0068 30%, #cc4049 90%)',
                        '&:hover': {
                            background: 'linear-gradient(45deg, #1565c0 30%, #0a0068 90%)',
                        }
                    }}
                >
                    {feedback ? 'Update' : 'Create'} Feedback
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default FeedbackDialog;
